<template>
	<div
		v-if="enableBankSelect"
		:class="{
			'plaid-account-select': true,
			'bank-select': true,
			styled: !disableStyling,
		}"
	>
		<div
			v-if="enableMultiSelect"
			class="multi-select-list"
		>
			<InputCheckbox
				v-for="bank in availableBankAccounts"
				:key="bank.plaidItemId"
				:class="{ selected: isBankSelected(bank.plaidItemId) }"
				:label="bank.institution"
				:modelValue="isBankSelected(bank.plaidItemId)"
				:disabled="bank.status !== 0"
				@click="selectBank(bank.plaidItemId)"
			/>
		</div>
		<div
			v-else
			class="single-select-list"
		>
			<InputRadio
				v-for="bank in availableBankAccounts"
				:key="bank.plaidItemId"
				:class="{ selected: isBankSelected(bank.plaidItemId) }"
				:label="bank.institution"
				:modelValue="bank.plaidItemId"
				:valueMatch="selectedAccountSingleSelect.plaidItemId"
				:disabled="bank.status !== 0"
				@click="selectBank(bank.plaidItemId)"
			/>
		</div>
	</div>

	<AccordionList
		v-else
		:list="availableBankAccounts"
		:class="{ 'plaid-account-select': true, styled: !disableStyling }"
		listLabel="institution"
		:closeOtherOnOpen="!enableMultiSelect"
	>
		<template #listHeader="{ headerItem }">
			<button
				v-if="!disableLaunchLinkButtons && headerItem.status !== 0"
				class="button-relogin"
				@click="$emit('launchLinkUpdate', headerItem)"
			>
				Re-Login
			</button>

			<b
				v-else-if="!enableMultiSelect && isBankSelected(headerItem.plaidItemId)"
				class="selected-account-name"
			>
				{{ getAccountNameByBank(headerItem.plaidItemId) }}
			</b>

			<b
				v-else-if="enableMultiSelect && isBankSelected(headerItem.plaidItemId)"
				class="multi-select-count"
			>
				{{ getNumSelectedByBank(headerItem.plaidItemId) }}
			</b>
		</template>

		<template #listBody="{ bodyItem }">
			<fieldset :disabled="bodyItem.status !== 0">
				<!--Multi-Select-->
				<div
					v-if="enableMultiSelect"
					class="multi-select-list"
				>
					<div
						v-show="bodyItem.status === 0 && bodyItem.accounts.length > 1"
						class="toggle-all-buttons"
					>
						<button
							class="button-select-all"
							@click="selectAllInBank(bodyItem.plaidItemId, true)"
						>
							Select All
						</button>
						<button
							class="button-deselect-all"
							@click="selectAllInBank(bodyItem.plaidItemId, false)"
						>
							Deselect All
						</button>
					</div>

					<InputCheckbox
						v-for="account in bodyItem.accounts"
						:key="account.accountId"
						:class="{ selected: isAccountSelected(account.accountId) }"
						:label="`${account.name} (...${account.mask})`"
						:modelValue="isAccountSelected(account.accountId)"
						@click="selectAccount(bodyItem.plaidItemId, account.accountId)"
					/>
				</div>

				<!--Single-Select-->
				<div
					v-else
					class="single-select-list"
				>
					<InputRadio
						v-for="account in bodyItem.accounts"
						:key="account.accountId"
						:class="{ selected: isAccountSelected(account.accountId) }"
						:label="`${account.name} (...${account.mask})`"
						:modelValue="account.accountId"
						:valueMatch="selectedAccountSingleSelect.accountId"
						@click="selectAccount(bodyItem.plaidItemId, account.accountId)"
					/>
				</div>

				<b
					v-if="bodyItem.accounts.length < 1"
					style="display: block"
				>
					No valid accounts.
				</b>

				<button
					v-if="!disableLaunchLinkButtons"
					class="button-link button-add-account"
					@click="$emit('launchLinkUpdate', bodyItem)"
				>
					<i class="fas fa-plus-circle" /> Add Another Account
				</button>
			</fieldset>
		</template>
	</AccordionList>
</template>
<script lang="ts">
	export interface IBankAccountInfo {
		accounts: PlaidBankAccount[];
		institution: string;
		status: number;
		plaidItemId: string;
	}

	export interface IPlaidAccountSelectProps {
		plaidCustomerInfo: PlaidCustomerInfo;

		/** Array of ENUMS.PLAID_PRODUCTS; filters out banks that don't have products active */
		productIncludeFilter?: string[];

		/** Array of ENUMS.PLAID_PRODUCTS; filters out banks that DO have the products active */
		productExcludeFilter?: string[];

		/** Limits shown accounts to only those compatible with the Plaid Transfer product */
		filterAccountsForTransfer?: boolean;

		/** Allows user to select entire bank accounts instead of individual sub-accounts */
		enableBankSelect?: boolean;

		/** When true, allows user to select multiple accounts instead of just one */
		enableMultiSelect?: boolean;

		/** Hides all buttons for updating Access Tokens ("Re-Login" and "Add Account") */
		disableLaunchLinkButtons?: boolean;

		/** When true, removes all internal styling on component */
		disableStyling?: boolean;
	}
</script>
<script setup lang="ts">
	// Given a plaidCustomerInfo object, provides UI for selecting one/many bank accounts
	import { computed, ref, watch } from 'vue';

	import util from '@core/services/util';

	import { PlaidAccessToken, PlaidCustomerInfo } from '@core/classes/Plaid/PlaidCustomerInfo';
	import ENUMS from '@core/classes/Enums';
	import { PlaidBankAccount } from '@core/classes/Plaid/PlaidBankAccount';

	import AccordionList from '@core/components/AccordionList.vue';
	import InputCheckbox from '@core/components/InputCheckbox.vue';
	import InputRadio from '@core/components/InputRadio.vue';

	const props = defineProps<IPlaidAccountSelectProps>();

	const emit = defineEmits<{
		/** Emitted when a button is clicked that should cause Link to be launched in Update Mode (re-login or adding accounts)
		 * Returns the access token object of the Plaid item to be updated */
		launchLinkUpdate: [plaidAccessToken: PlaidAccessToken];
		selectedAccountUpdate: [selectedAccounts: { plaidItemId: string; accountId: string }[]];
	}>();

	const selectedAccounts = ref<{ plaidItemId: string; accountId: string }[]>([]);

	const availableBankAccounts = computed((): IBankAccountInfo[] => {
		if (!props.plaidCustomerInfo?.accessTokens || props.plaidCustomerInfo.accessTokens.length < 1) return [];

		let accessTokens = util.clone(props.plaidCustomerInfo.accessTokens) as PlaidAccessToken[];

		if (props.productIncludeFilter.length > 0 || props.productExcludeFilter.length > 0 || props.filterAccountsForTransfer) {
			const productsToInclude = props.filterAccountsForTransfer
				? props.productIncludeFilter.concat([ENUMS.PLAID_PRODUCTS.Transfer, ENUMS.PLAID_PRODUCTS.Auth])
				: props.productIncludeFilter;

			accessTokens = accessTokens.filter(at => {
				let anyAcceptedProducts = false;
				for (const p of at.activeProducts) {
					// If ANY match in exclude list, reject the entire token
					if (props.productExcludeFilter.includes(p)) return false;

					// If there's no include filters OR if ONE product matches filter, flag as accepted.
					if (productsToInclude.length < 1 || productsToInclude.includes(p)) anyAcceptedProducts = true;
				}

				return anyAcceptedProducts;
			});
		}

		return accessTokens.map(at => {
			let validAccounts = at.accounts as PlaidBankAccount[];
			if (props.filterAccountsForTransfer) validAccounts = validAccounts.filter(a => a.subtype === 'checking' || 'savings');

			return {
				accounts: validAccounts,
				institution: at.institution.name,
				status: at.status,
				plaidItemId: at.plaidItemId,
			};
		});
	});

	const selectedAccountSingleSelect = computed((): { plaidItemId: string; accountId: string } => {
		if (props.enableMultiSelect || selectedAccounts.value.length < 1) return { plaidItemId: null, accountId: null };
		return selectedAccounts.value[0];
	});

	watch(selectedAccounts, newVal => {
		emit('selectedAccountUpdate', newVal);
	});

	const getBankById = (plaidItemId: string): IBankAccountInfo => {
		return availableBankAccounts.value.find(b => b.plaidItemId === plaidItemId);
	};

	const getAccountById = (plaidItemId: string, accountId: string): PlaidBankAccount => {
		const bank = getBankById(plaidItemId);
		if (!bank) return null;

		return bank.accounts.find(a => a.accountId === accountId);
	};

	const getSelectedBank = (accountId: string): IBankAccountInfo => {
		const plaidItemId = selectedAccounts.value.find(a => a.accountId === accountId)?.plaidItemId;
		if (!plaidItemId) return null;

		return getBankById(plaidItemId);
	};

	const getSelectedAccount = (accountId: string): PlaidBankAccount => {
		const plaidItemId = selectedAccounts.value.find(a => a.accountId === accountId)?.plaidItemId;
		if (!plaidItemId) return null;

		return getAccountById(plaidItemId, accountId);
	};

	const getAccountNameByBank = (plaidItemId: string): string => {
		const selectedAccount = selectedAccounts.value.find(a => a.plaidItemId === plaidItemId)?.accountId;
		if (!selectedAccount) return null;

		return getAccountById(plaidItemId, selectedAccount)?.name;
	};

	const getNumSelectedByBank = (plaidItemId: string): number => {
		return selectedAccounts.value.reduce((count, currItem) => (currItem.plaidItemId === plaidItemId ? count + 1 : count), 0);
	};

	const isBankSelected = (plaidItemId: string): boolean => {
		return selectedAccounts.value.some(a => a.plaidItemId === plaidItemId);
	};

	const isAccountSelected = (accountId: string): boolean => {
		return selectedAccounts.value.some(a => a.accountId === accountId);
	};

	const selectAccount = (plaidItemId: string, accountId: string): void => {
		if (isAccountSelected(accountId)) removeSelectedAccount(plaidItemId, accountId);
		else addSelectedAccount(plaidItemId, accountId);
	};

	const selectBank = (plaidItemId: string): void => {
		if (isBankSelected(plaidItemId)) removeSelectedAccount(plaidItemId, '');
		else addSelectedAccount(plaidItemId, '');
	};

	const selectAllInBank = (plaidItemId: string, isAddAll: boolean): void => {
		if (isAddAll) {
			const bank = getBankById(plaidItemId);
			bank?.accounts?.forEach(a => {
				if (!isAccountSelected(a.accountId)) addSelectedAccount(plaidItemId, a.accountId);
			});
		} else {
			selectedAccounts.value = selectedAccounts.value.filter(a => a.plaidItemId !== plaidItemId);
		}
	};

	const addSelectedAccount = (plaidItemId: string, accountId: string): void => {
		if (props.enableMultiSelect || selectedAccounts.value.length < 1) selectedAccounts.value.push({ plaidItemId, accountId });
		else selectedAccounts.value.splice(0, 1, { plaidItemId, accountId });
	};

	const removeSelectedAccount = (plaidItemId: string, accountId: string): void => {
		selectedAccounts.value = selectedAccounts.value.filter(a => a.plaidItemId !== plaidItemId || a.accountId !== accountId);
	};
</script>
<style>
	.plaid-account-select.styled .button-relogin {
		background-color: var(--print-color);
		animation: shadowPulse 1.5s infinite;
	}

	.plaid-account-select.styled .button-relogin:hover {
		animation: unset;
	}

	.plaid-account-select.bank-select.styled .multi-select-list,
	.plaid-account-select.bank-select.styled .single-select-list {
		font-weight: bold;
		font-size: 1.3rem;
	}

	.plaid-account-select.bank-select.styled fieldset {
		margin: 10px 0;
		padding: 10px;
		border: 1px solid var(--border-color);
		border-radius: 10px;
		transition: all 0.3s ease-in-out;
	}
	.plaid-account-select.bank-select.styled fieldset.selected {
		margin-left: 10px;
		border: 2px solid var(--accept-color);
		box-shadow: 3px 4px 4px 1px rgba(0, 0, 0, 0.2);
	}

	.plaid-account-select.styled .Accordion {
		margin-bottom: 10px;
		border-radius: 10px;
		transition: all 0.3s ease-in-out;
		overflow: hidden;
	}

	.plaid-account-select.styled .Accordion:has(.button-relogin) {
		box-shadow: inset 3px 4px 4px 1px rgba(0, 0, 0, 0.2);
		color: var(--disabled-color);
	}

	.plaid-account-select.styled .Accordion:has(.selected) {
		width: unset;
		margin-left: 10px;
		border: 2px solid var(--accept-color);
		box-shadow: 3px 4px 4px 1px rgba(0, 0, 0, 0.2);
	}

	.plaid-account-select.styled .Accordion .accordion-header .accordion-label {
		font-weight: bold;
		font-size: 1.3em;
	}

	.plaid-account-select.styled .Accordion .accordion-header .multi-select-count {
		margin: 5px;
		height: 25px;
		width: 25px;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 2px solid var(--main-color);
		border-radius: 100%;
		color: var(--main-color);
	}

	.plaid-account-select.styled .Accordion .children-container {
		margin: 15px;
	}

	.plaid-account-select.styled .Accordion .children-container fieldset > *:not(:last-child) {
		margin-bottom: 10px;
	}

	.plaid-account-select.styled .Accordion .children-container .selected {
		color: var(--main-color);
		font-weight: bold;
		transition: all 0.3s ease-in-out;
	}

	.plaid-account-select.styled .Accordion .children-container .input-radio {
		margin: 5px 0;
	}

	.plaid-account-select.styled .Accordion .children-container .toggle-all-buttons {
		display: flex;
		gap: 10px;
	}

	.plaid-account-select.styled .Accordion .children-container .toggle-all-buttons button {
		background-color: var(--accept-color);
	}

	.plaid-account-select.styled .Accordion .children-container .toggle-all-buttons .button-deselect-all {
		filter: hue-rotate(180deg);
	}

	@keyframes shadowPulse {
		0% {
			transform: scale(0.98);
			box-shadow: 0 0 0 0 rgba(247, 150, 70, 0.7);
		}

		70% {
			transform: scale(1);
			box-shadow: 0 0 0 10px rgba(247, 150, 70, 0);
		}

		100% {
			transform: scale(0.98);
			box-shadow: 0 0 0 0 rgba(247, 150, 70, 0);
		}
	}
</style>
